import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, X, Search, ChevronDown, Star, StarOff, Save, RefreshCw, ArrowLeft } from 'lucide-react';
import { faqService, faqCategoryService } from '../../../services/faqService';
import toast from 'react-hot-toast';

const MODULE_TYPES = [
  { value: 'admin', label: 'Admin', color: 'green' },
  { value: 'patient', label: 'Patient', color: 'blue' },
  { value: 'doctor', label: 'Doctor', color: 'purple' },
  { value: 'clinician', label: 'Clinician', color: 'orange' }
];

const AdminFaqManagementPage = () => {
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState('patient');
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [faqCounts, setFaqCounts] = useState({
    admin: 0,
    patient: 0,
    doctor: 0,
    clinician: 0
  });
  
  // Modal states
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Form states
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    module_type: 'patient',
    category_id: null,
    is_featured: false,
    display_order: 0
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    module_type: 'patient',
    description: '',
    display_order: 0
  });

  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - FAQ Management';
  }, []);

  // Fetch FAQ counts for all modules on initial load
  const fetchAllCounts = useCallback(async () => {
    try {
      const [adminRes, patientRes, doctorRes, clinicianRes] = await Promise.all([
        faqService.getByModule('admin'),
        faqService.getByModule('patient'),
        faqService.getByModule('doctor'),
        faqService.getByModule('clinician')
      ]);
      
      setFaqCounts({
        admin: adminRes.total_faqs || 0,
        patient: patientRes.total_faqs || 0,
        doctor: doctorRes.total_faqs || 0,
        clinician: clinicianRes.total_faqs || 0
      });
    } catch (error) {
      console.error('Error fetching FAQ counts:', error);
    }
  }, []);

  // Fetch FAQs and categories for active tab
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [faqsResponse, categoriesResponse] = await Promise.all([
        faqService.getByModule(activeTab),
        faqCategoryService.getByModule(activeTab)
      ]);
      
      setFaqs(faqsResponse.faqs || []);
      setCategories(categoriesResponse || []);
      
      // Update count for current tab
      setFaqCounts(prev => ({
        ...prev,
        [activeTab]: faqsResponse.total_faqs || 0
      }));
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Fetch all counts on mount
  useEffect(() => {
    fetchAllCounts();
  }, [fetchAllCounts]);

  // Fetch data when tab changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter FAQs
  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = !searchTerm || 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || faq.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // FAQ CRUD handlers
  const handleAddFaq = () => {
    setEditingFaq(null);
    setFaqForm({
      question: '',
      answer: '',
      module_type: activeTab,
      category_id: null,
      is_featured: false,
      display_order: faqs.length
    });
    setShowFaqModal(true);
  };

  const handleEditFaq = (faq) => {
    setEditingFaq(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      module_type: faq.module_type,
      category_id: faq.category_id,
      is_featured: faq.is_featured,
      display_order: faq.display_order
    });
    setShowFaqModal(true);
  };

  const handleSaveFaq = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }

    try {
      if (editingFaq) {
        await faqService.update(editingFaq.id, faqForm);
        toast.success('FAQ updated successfully');
      } else {
        await faqService.create(faqForm);
        toast.success('FAQ created successfully');
      }
      setShowFaqModal(false);
      fetchData();
      // Refresh all counts since FAQ could be for any module
      fetchAllCounts();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error(error.response?.data?.detail || 'Failed to save FAQ');
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    
    try {
      await faqService.delete(faqId);
      toast.success('FAQ deleted successfully');
      fetchData();
      // Refresh all counts
      fetchAllCounts();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ');
    }
  };

  const handleToggleFeatured = async (faq) => {
    try {
      await faqService.update(faq.id, { is_featured: !faq.is_featured });
      toast.success(`FAQ ${!faq.is_featured ? 'featured' : 'unfeatured'}`);
      fetchData();
    } catch (error) {
      console.error('Error updating FAQ:', error);
      toast.error('Failed to update FAQ');
    }
  };

  // Category CRUD handlers
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      module_type: activeTab,
      description: '',
      display_order: categories.length
    });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      module_type: category.module_type,
      description: category.description || '',
      display_order: category.display_order
    });
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      if (editingCategory) {
        await faqCategoryService.update(editingCategory.id, categoryForm);
        toast.success('Category updated successfully');
      } else {
        await faqCategoryService.create(categoryForm);
        toast.success('Category created successfully');
      }
      setShowCategoryModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? FAQs in this category will become uncategorized.')) return;
    
    try {
      await faqCategoryService.delete(categoryId);
      toast.success('Category deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const getModuleColor = (moduleType) => {
    const module = MODULE_TYPES.find(m => m.value === moduleType);
    return module?.color || 'gray';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-2 px-6 pb-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">FAQ Management</h1>
            <p className="text-gray-600 text-sm">Manage FAQs for all modules</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin/faqs')}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:border-gray-400 hover:shadow-sm active:bg-gray-200 transition-all duration-200"
              title="Back to FAQs"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              onClick={handleAddCategory}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 hover:shadow-md active:bg-gray-800 transition-all duration-200"
            >
              <Plus className="h-4 w-4" />
              Add Category
            </button>
            <button
              onClick={handleAddFaq}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 hover:shadow-md active:bg-blue-800 transition-all duration-200"
            >
              <Plus className="h-4 w-4" />
              Add FAQ
            </button>
          </div>
        </div>

        {/* Module Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b">
            {MODULE_TYPES.map((module) => (
              <button
                key={module.value}
                onClick={() => {
                  setActiveTab(module.value);
                  setSelectedCategory(null);
                  setSearchTerm('');
                }}
                className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
                  activeTab === module.value
                    ? `text-${module.color}-600 border-b-2 border-${module.color}-600 bg-${module.color}-50`
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                style={{
                  borderColor: activeTab === module.value ? `var(--${module.color}-600, ${module.color === 'blue' ? '#2563eb' : module.color === 'green' ? '#16a34a' : module.color === 'purple' ? '#9333ea' : '#ea580c'})` : 'transparent',
                  color: activeTab === module.value ? (module.color === 'blue' ? '#2563eb' : module.color === 'green' ? '#16a34a' : module.color === 'purple' ? '#9333ea' : '#ea580c') : undefined
                }}
              >
                {module.label} FAQs
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                  {faqCounts[module.value] || 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex gap-4 items-center">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Category Filter */}
            <div className="w-64">
              <select
                value={selectedCategory || ''}
                onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name} ({cat.faq_count || 0})
                  </option>
                ))}
              </select>
            </div>
            
            {/* Refresh */}
            <button
              onClick={fetchData}
              className="p-2 text-gray-500 bg-white border border-gray-300 rounded-lg hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 hover:shadow-sm active:bg-blue-100 transition-all duration-200"
              title="Refresh"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Categories Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Categories</h3>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <div
                key={category.id}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg group"
              >
                <span className="text-sm font-medium text-gray-700">{category.name}</span>
                <span className="text-xs text-gray-500">({category.faq_count || 0})</span>
                <div className="hidden group-hover:flex items-center gap-1 ml-2">
                  <button
                    onClick={() => handleEditCategory(category)}
                    className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-all duration-150"
                    title="Edit category"
                  >
                    <Edit2 className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-all duration-150"
                    title="Delete category"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
            {categories.length === 0 && (
              <p className="text-gray-500 text-sm">No categories yet. Add one to organize your FAQs.</p>
            )}
          </div>
        </div>

        {/* FAQs List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">
              FAQs ({filteredFaqs.length})
            </h3>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredFaqs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No FAQs found. {searchTerm || selectedCategory ? 'Try adjusting your filters.' : 'Add your first FAQ!'}
            </div>
          ) : (
            <div className="divide-y">
              {filteredFaqs.map((faq) => (
                <div key={faq.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {faq.is_featured && (
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        )}
                        <h4 className="font-medium text-gray-800">{faq.question}</h4>
                      </div>
                      <p className="text-gray-600 text-sm mb-2">{faq.answer}</p>
                      <div className="flex items-center gap-2">
                        {faq.category_name && (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            {faq.category_name}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          Order: {faq.display_order}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleFeatured(faq)}
                        className={`p-2 rounded-lg transition-all duration-200 ${
                          faq.is_featured 
                            ? 'text-yellow-500 hover:bg-yellow-100 hover:shadow-sm active:bg-yellow-200' 
                            : 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50 hover:shadow-sm active:bg-yellow-100'
                        }`}
                        title={faq.is_featured ? 'Remove from featured' : 'Mark as featured'}
                      >
                        {faq.is_featured ? <Star className="h-4 w-4 fill-current" /> : <StarOff className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleEditFaq(faq)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 hover:shadow-sm active:bg-blue-100 rounded-lg transition-all duration-200"
                        title="Edit FAQ"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteFaq(faq.id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 hover:shadow-sm active:bg-red-100 rounded-lg transition-all duration-200"
                        title="Delete FAQ"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* FAQ Modal */}
      {showFaqModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
              </h3>
              <button
                onClick={() => setShowFaqModal(false)}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-all duration-150"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question *
                </label>
                <input
                  type="text"
                  value={faqForm.question}
                  onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter the FAQ question"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Answer *
                </label>
                <textarea
                  value={faqForm.answer}
                  onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter the FAQ answer"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Module *
                  </label>
                  <select
                    value={faqForm.module_type}
                    onChange={(e) => {
                      setFaqForm({ ...faqForm, module_type: e.target.value, category_id: null });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {MODULE_TYPES.map((module) => (
                      <option key={module.value} value={module.value}>
                        {module.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={faqForm.category_id || ''}
                    onChange={(e) => setFaqForm({ ...faqForm, category_id: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No Category</option>
                    {categories
                      .filter(c => c.module_type === faqForm.module_type)
                      .map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={faqForm.display_order}
                    onChange={(e) => setFaqForm({ ...faqForm, display_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={faqForm.is_featured}
                      onChange={(e) => setFaqForm({ ...faqForm, is_featured: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Featured FAQ</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowFaqModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:border-gray-400 hover:shadow-sm active:bg-gray-200 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFaq}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 hover:shadow-md active:bg-blue-800 transition-all duration-200"
              >
                <Save className="h-4 w-4" />
                {editingFaq ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h3>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-all duration-150"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter category name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Module *
                </label>
                <select
                  value={categoryForm.module_type}
                  onChange={(e) => setCategoryForm({ ...categoryForm, module_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={!!editingCategory}
                >
                  {MODULE_TYPES.map((module) => (
                    <option key={module.value} value={module.value}>
                      {module.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  min="0"
                  value={categoryForm.display_order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:border-gray-400 hover:shadow-sm active:bg-gray-200 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 hover:shadow-md active:bg-blue-800 transition-all duration-200"
              >
                <Save className="h-4 w-4" />
                {editingCategory ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFaqManagementPage;
